import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { usePostHog } from "@posthog/react";
import {
  DEFAULT_INTERVAL,
  PRICES,
  YEARLY_SAVING,
  type BillingInterval,
} from "../config/plans";
import { fetchAccount, openBillingPortal, startCheckout, type Account } from "../billing";
import { beginGoogleSignIn, clearHostSession, isWebAuthConfigured } from "../webAuth";
import { rememberUpgradeReturn } from "../upgradeReturn";
import { CHECKOUT_CLICKED, PRO_PAYWALL_DISMISSED } from "../analytics/events";
import { PRIVACY_POLICY_URL, TERMS_URL } from "../config/legal";

// The Pro plans card: pick monthly or yearly, then straight to Stripe.
//
// ONE card, two homes, so the two can never quote different prices or offer
// different things:
//
//   variant="dialog" — the host controller's paywall overlay. The host is
//     presenting, so Stripe opens in a NEW tab and this one keeps the room.
//   variant="page"   — /upgrade, where the lifecycle emails land. Nothing to
//     protect here, so Stripe takes over the tab.
//
// Styled with the host controller's tokens (the parent emits them — see
// ../theme hostThemeTokens), so the overlay and the page read as the same thing.

export type ProPlansVariant = "dialog" | "page";

type Props = {
  variant: ProPlansVariant;
  /** Analytics + Stripe metadata: where the host came from. */
  source: string;
  initialInterval?: BillingInterval;
  /** Dialog only: dismiss the overlay. */
  onClose?: () => void;
  /** Page only: back from Stripe, waiting for the webhook to grant Pro. */
  awaitingPayment?: boolean;
  /** Page only: back from Stripe without paying. */
  canceled?: boolean;
  /** Where the Google sign-in comes back to. Defaults to /upgrade. */
  signInReturnPath?: (interval: BillingInterval) => string;
};

// ── Layout ───────────────────────────────────────────────────────────────
// Two bands — the offer (pitch, period, price) over a rule, then what you get
// and the action. The band does the hierarchy work: without it the price read
// as a heading for the features rather than the cost of the pitch above it.

const Card = styled.div<{ $variant: ProPlansVariant }>`
  width: 100%;
  max-width: 460px;
  box-sizing: border-box;
  background: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 1px 2px var(--shadow-near),
    0 24px 60px -16px var(--shadow-far);
  text-align: left;

  ${(p) =>
    p.$variant === "dialog" &&
    css`
      animation: copresentModalPop 0.16s ease-out;
      /* Phones: a full-screen sheet, not a floating card. The plans, features
         and CTA outrun a short viewport, which a fixed card clips with no way
         to scroll. dvh, not vh: mobile Safari's vh includes the collapsing URL
         bar, which would push the CTA under it. */
      @media (max-width: 767px) {
        max-width: none;
        min-height: 100dvh;
        border: none;
        border-radius: 0;
        box-shadow: none;
        overflow-y: auto;
        animation: copresentSheetUp 0.2s ease-out;
      }
      @keyframes copresentModalPop {
        from { opacity: 0; transform: translateY(8px) scale(0.98); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }
      @keyframes copresentSheetUp {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
    `}
`;

const Offer = styled.div<{ $variant: ProPlansVariant }>`
  position: relative;
  padding: 26px 26px 22px;
  border-bottom: 1px solid var(--border-soft);

  ${(p) =>
    p.$variant === "dialog" &&
    css`
      @media (max-width: 767px) {
        /* Clear of the notch, and of the close button. */
        padding-top: calc(22px + env(safe-area-inset-top));
        padding-right: 62px;
      }
    `}
`;

// Mobile-only escape hatch: full-bleed on a phone there is no scrim to click,
// so the sheet would be a trap without it. Desktop has the scrim + "Not now".
const Close = styled.button`
  display: none;
  @media (max-width: 767px) {
    position: absolute;
    top: calc(18px + env(safe-area-inset-top));
    right: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    appearance: none;
    border: none;
    border-radius: 50%;
    background: var(--bg-fallback);
    color: var(--text-muted);
    cursor: pointer;
  }
`;

const Eyebrow = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--link);
`;

const Title = styled.h2`
  margin: 9px 0 0;
  font-size: 23px;
  line-height: 1.22;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-headline);
`;

const Subtitle = styled.p`
  margin: 7px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-muted);
`;

// A two-option segmented control. Radio semantics, because it is exactly that:
// one of two, always one selected.
const Periods = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  margin-top: 18px;
  padding: 4px;
  border-radius: 12px;
  background: var(--bg-fallback);
  border: 1px solid var(--border-soft);

  button {
    appearance: none;
    border: 0;
    border-radius: 9px;
    padding: 9px 10px;
    font: inherit;
    font-size: 14px;
    font-weight: 600;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    transition: background-color 0.12s, color 0.12s;
  }
  button:hover {
    color: var(--text-primary);
  }
  button[aria-checked="true"] {
    background: var(--bg-card);
    color: var(--text-headline);
    box-shadow: 0 1px 3px var(--shadow-near);
  }
  button:focus-visible {
    outline: 2px solid var(--link);
    outline-offset: 2px;
  }
`;

const Save = styled.span`
  font-size: 11px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 999px;
  color: #16a34a;
  background: rgba(34, 197, 94, 0.14);
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 9px;
  margin-top: 18px;
`;

// Leads with the per-month figure so monthly and yearly compare like with like;
// the note beside it and the button name the real charge.
const Price = styled.span`
  font-size: 40px;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  color: var(--text-headline);
`;

const PriceNote = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-muted);
`;

const Body = styled.div<{ $variant: ProPlansVariant }>`
  padding: 20px 26px 24px;
  ${(p) =>
    p.$variant === "dialog" &&
    css`
      @media (max-width: 767px) {
        padding-bottom: calc(24px + env(safe-area-inset-bottom));
      }
    `}
`;

const FeatureHeading = styled.h3`
  margin: 0 0 12px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--text-fade);
`;

const Features = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 11px;

  li {
    display: flex;
    align-items: flex-start;
    gap: 11px;
    font-size: 14px;
    line-height: 1.45;
    color: var(--text-secondary);
  }
`;

// Accent, not green: these are things you'd be buying, not things done.
const Tick = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  flex: 0 0 auto;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.16);
  color: var(--link);
`;

const Action = styled.div`
  margin-top: 22px;
`;

const Cta = styled.button`
  display: block;
  width: 100%;
  box-sizing: border-box;
  appearance: none;
  border: none;
  border-radius: 11px;
  padding: 14px 16px;
  font: inherit;
  font-size: 15.5px;
  font-weight: 700;
  color: #fff;
  background: var(--link);
  cursor: pointer;
  /* The one thing on the card that should look pressable. */
  box-shadow: 0 6px 18px -6px rgba(99, 102, 241, 0.65);
  transition: background-color 0.12s, box-shadow 0.12s, transform 0.04s, opacity 0.12s;

  &:hover:enabled {
    background: var(--link-hover);
  }
  &:active:enabled {
    transform: translateY(1px);
  }
  &:disabled {
    opacity: 0.55;
    cursor: default;
  }
  &:focus-visible {
    outline: 3px solid var(--link);
    outline-offset: 3px;
  }
`;

const Secondary = styled.button`
  display: block;
  margin: 8px auto 0;
  width: 100%;
  appearance: none;
  border: none;
  background: none;
  border-radius: 10px;
  padding: 10px 16px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  &:hover:enabled {
    color: var(--text-primary);
  }
`;

const StatusTitle = styled.h3`
  margin: 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: var(--text-headline);
`;

const Text = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.5;
  color: var(--text-secondary);

  a {
    color: var(--link);
  }
`;

const FinePrint = styled.p`
  margin: 11px 0 0;
  font-size: 12px;
  line-height: 1.5;
  color: var(--text-fade);
  text-align: center;
  overflow-wrap: anywhere;

  a {
    color: inherit;
  }
`;

const Alert = styled.div`
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 14px;
  line-height: 1.45;
  color: var(--text-primary);
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.28);

  p {
    margin: 0 0 10px;
  }
`;

const RetryButton = styled.button`
  appearance: none;
  border: 1px solid var(--border-chip);
  background: var(--bg-card);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 7px 12px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CloseIcon = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

// What Pro adds over Free (COP-33). Previews, notes, the timer and Q&A are on
// every plan now, so they are not listed — a paywall that sells what the host
// already has reads as a trick. Copresenting leads: every entry point fires when
// a copresenter has just been refused, because the free sessions ran out.
const FEATURES = [
  "Copresent every time — invite as many copresenters as you like",
  "Unlimited presentations, every month",
  "Everything in Free — previews, notes, timer and Q&A",
];

const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "long" }) : null;

// A blank tab opened INSIDE the click, then pointed at Stripe once the server
// answers. Opening it after the await would be a popup the browser blocks —
// the click's user activation is spent by then.
const openPendingTab = (): Window | null => {
  const tab = window.open("", "_blank");
  if (!tab) return null;
  try {
    tab.opener = null;
    tab.document.title = "Opening checkout…";
    tab.document.body.style.cssText =
      "font:15px system-ui,sans-serif;color:#6b7280;display:grid;place-items:center;height:100vh;margin:0";
    tab.document.body.textContent = "Opening secure checkout…";
  } catch {
    // Cosmetic only.
  }
  return tab;
};

export const ProPlans = ({
  variant,
  source,
  initialInterval = DEFAULT_INTERVAL,
  onClose,
  awaitingPayment = false,
  canceled = false,
  signInReturnPath = (interval) => `/upgrade?source=${source}&interval=${interval}`,
}: Props) => {
  const posthog = usePostHog();
  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [interval, setBillingInterval] = useState<BillingInterval>(initialInterval);
  // A checkout the browser wouldn't let us open in a new tab: offered as a
  // plain link instead, which the click itself is allowed to open.
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // The paywall's negative branch: the dialog went away without a checkout.
  // Reported when it unmounts, because that is the one place every way out
  // meets — the close button, "Not now", Esc, a backdrop click (which the
  // parent handles), leaving the page. Once per open by construction. A host
  // who started checkout, or who is already Pro, didn't turn anything down.
  const declined = useRef({ checkout: false, pro: false, source, posthog });
  declined.current.source = source;
  declined.current.posthog = posthog;
  useEffect(() => {
    if (variant !== "dialog") return;
    const state = declined.current;
    return () => {
      if (!state.checkout && !state.pro) {
        state.posthog?.capture(PRO_PAYWALL_DISMISSED, { source: state.source });
      }
    };
  }, [variant]);

  // Esc closes the dialog, like every other modal.
  useEffect(() => {
    if (variant !== "dialog" || !onClose) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [variant, onClose]);

  // Back from Stripe: poll until the webhook has granted Pro.
  useEffect(() => {
    if (!awaitingPayment || account?.planSource === "paid") return;
    const timer = window.setInterval(() => setAttempt((n) => n + 1), 3000);
    return () => window.clearInterval(timer);
  }, [awaitingPayment, account?.planSource]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading((was) => was && attempt === 0);
    fetchAccount(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setAccount(result);
          setError(null);
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) setError("We couldn't check your account. Please try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [attempt]);

  const signIn = async () => {
    setBusy(true);
    setError(null);
    try {
      await beginGoogleSignIn(signInReturnPath(interval));
    } catch {
      setError("Sign-in couldn't start. Please try again.");
      setBusy(false);
    }
  };

  const buy = async () => {
    setBusy(true);
    setError(null);
    setBlockedUrl(null);
    const tab = variant === "dialog" ? openPendingTab() : null;
    const done = () => tab?.close();
    try {
      // Recheck at the purchase boundary: the account could have changed, or
      // bought in another tab, since this card was opened.
      const current = await fetchAccount();
      setAccount(current);
      if (!current || current.planSource === "paid") return done();
      if (current.sub !== account?.sub) {
        setError("Your signed-in account changed. Check the account below before continuing.");
        return done();
      }
      posthog?.identify(current.sub);
      declined.current.checkout = true;
      posthog?.capture(CHECKOUT_CLICKED, { source, plan: interval });
      // Leave a trail /upgrade/success follows back: the deck from the
      // overlay, this page otherwise, where it waits for Pro to land.
      rememberUpgradeReturn(variant === "dialog" ? window.location.pathname : "/upgrade");
      const result = await startCheckout(interval, source);
      if (result.ok) {
        if (variant === "page") {
          window.location.assign(result.url);
          return;
        }
        if (tab && !tab.closed) tab.location.href = result.url;
        else setBlockedUrl(result.url);
        return;
      }
      done();
      if (result.error === "already_pro") {
        // Bought elsewhere, or a subscription with a failing card: the status
        // below says which and offers the portal.
        setAccount(await fetchAccount());
        return;
      }
      setError(
        result.error === "billing_unavailable"
          ? "Checkout is temporarily unavailable. Please try again later."
          : "We couldn't start checkout. Please try again.",
      );
    } catch {
      done();
      setError("We couldn't check your account. Please try again.");
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const manage = async () => {
    setBusy(true);
    setError(null);
    posthog?.capture("billing_portal_opened", { source });
    const tab = variant === "dialog" ? openPendingTab() : null;
    const result = await openBillingPortal();
    if (result.ok) {
      if (variant === "page") return window.location.assign(result.url);
      if (tab && !tab.closed) tab.location.href = result.url;
      else setBlockedUrl(result.url);
    } else {
      tab?.close();
      setError("We couldn't open billing. Please try again, or email hello@copresent.app.");
    }
    if (mounted.current) setBusy(false);
  };

  const price = PRICES[interval];
  const subscription = account?.subscription ?? null;
  const renewal = formatDate(subscription?.currentPeriodEnd);
  const endsOn = formatDate(subscription?.paidThrough);
  const lifetime = account?.billing === "lifetime";
  const paid = !lifetime && account?.planSource === "paid";
  declined.current.pro = lifetime || paid;
  // A subscription that still exists but no longer entitles — the card is
  // failing past the grace. Selling a second one would double-bill, so send
  // them to the portal. One that has ENDED is not stuck: that account may
  // subscribe again like anyone else.
  const stuck = Boolean(
    account &&
      account.planSource !== "paid" &&
      account.canManageBilling &&
      subscription &&
      ["past_due", "unpaid"].includes(subscription.status),
  );
  const choosing = !loading && !lifetime && !paid && !stuck;
  const newTabNote = variant === "dialog" ? " · opens in a new tab, so your presentation keeps running" : "";

  const action = (() => {
    if (loading) return <Text role="status">Checking your account…</Text>;
    if (lifetime) {
      return <>
        <StatusTitle>You have lifetime Pro</StatusTitle>
        <Text>Thanks for being an early supporter. Pro is yours for good, including new Pro features. There is nothing to buy.</Text>
      </>;
    }
    if (paid) {
      const plan = subscription?.interval ? PRICES[subscription.interval].label : null;
      return <>
        <StatusTitle>You're on Pro{plan ? ` · ${plan}` : ""}</StatusTitle>
        <Text>{subscription?.cancelAtPeriodEnd
          ? `Your subscription is cancelled. Pro stays on until ${endsOn ?? "the end of the period"}.`
          : subscription?.status === "past_due"
            ? "Your last payment didn't go through. Update your card to keep Pro."
            : renewal ? `Renews on ${renewal}.` : "You're all set."}</Text>
        {account?.canManageBilling && (
          <Cta type="button" disabled={busy} onClick={manage}>{busy ? "Opening billing…" : "Manage billing"}</Cta>
        )}
      </>;
    }
    if (awaitingPayment && account) {
      return <>
        <StatusTitle>Activating your Pro account</StatusTitle>
        <Text role="status">We're waiting for payment confirmation. This usually takes a few seconds. If it takes longer, contact hello@copresent.app.</Text>
      </>;
    }
    if (stuck) {
      return <>
        <StatusTitle>Your subscription needs attention</StatusTitle>
        <Text>Your last payment didn't go through, so Pro is paused. Update your card to switch it back on.</Text>
        <Cta type="button" disabled={busy} onClick={manage}>{busy ? "Opening billing…" : "Update payment method"}</Cta>
      </>;
    }
    if (error) return null;
    if (!account) {
      if (!isWebAuthConfigured()) return <Text>Sign-in is temporarily unavailable. Please try again later.</Text>;
      return <>
        <Text>Sign in with the Google account you use for Copresent.</Text>
        <Cta type="button" disabled={busy} onClick={signIn}>{busy ? "Opening Google…" : "Continue with Google"}</Cta>
        <FinePrint>
          By continuing you agree to our{" "}
          <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms</a> and{" "}
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>.
        </FinePrint>
      </>;
    }
    return <>
      <Cta type="button" disabled={busy} onClick={buy}>
        {busy ? "Opening checkout…" : `Get Pro for ${price.amount} ${price.per}`}
      </Cta>
      <FinePrint>
        Upgrading <strong>{account.email}</strong>{newTabNote}. Secure payment on Stripe · cancel any time.
      </FinePrint>
    </>;
  })();

  return (
    <Card
      $variant={variant}
      {...(variant === "dialog"
        ? { role: "dialog", "aria-modal": true, "aria-labelledby": "pro-plans-title", "aria-describedby": "pro-plans-desc" }
        : { as: "section" as const, "aria-labelledby": "pro-plans-title" })}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <Offer $variant={variant}>
        {variant === "dialog" && onClose && (
          <Close type="button" aria-label="Close" title="Close" onClick={onClose}>
            <CloseIcon />
          </Close>
        )}
        <Eyebrow>Upgrade</Eyebrow>
        <Title id="pro-plans-title">Copresent Pro</Title>
        <Subtitle id="pro-plans-desc">
          {PRICES.month.amount} a month, or {PRICES.year.amount} a year ({YEARLY_SAVING.toLowerCase()}). Cancel any time.
        </Subtitle>
        {choosing && (
          <>
            <Periods role="radiogroup" aria-label="Billing period">
              {(["month", "year"] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={interval === option}
                  onClick={() => setBillingInterval(option)}
                >
                  {PRICES[option].label}
                  {option === "year" && <Save>{YEARLY_SAVING}</Save>}
                </button>
              ))}
            </Periods>
            <PriceRow>
              <Price>{price.perMonth}</Price>
              <PriceNote>/ month · {price.note}</PriceNote>
            </PriceRow>
          </>
        )}
      </Offer>

      <Body $variant={variant}>
        <FeatureHeading>What you get</FeatureHeading>
        <Features>
          {FEATURES.map((feature) => (
            <li key={feature}>
              <Tick><CheckIcon /></Tick>
              {feature}
            </li>
          ))}
        </Features>

        <Action>
          {action}
          {blockedUrl && (
            <Text role="status" style={{ marginTop: 12 }}>
              Your browser blocked the new tab.{" "}
              <Cta as="a" href={blockedUrl} target="_blank" rel="noopener" style={{ marginTop: 8, textAlign: "center", textDecoration: "none" }}>
                Continue to Stripe
              </Cta>
            </Text>
          )}
          {canceled && account && !awaitingPayment && !error && choosing && (
            <FinePrint role="status">Checkout was cancelled — you haven't been charged.</FinePrint>
          )}
          {error && (
            <Alert role="alert">
              <p>{error}</p>
              <RetryButton type="button" disabled={busy} onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </RetryButton>
            </Alert>
          )}
          {variant === "page" && account && (
            <Secondary
              type="button"
              disabled={busy}
              onClick={() => {
                clearHostSession();
                setAccount(null);
                void signIn();
              }}
            >
              Use another Google account
            </Secondary>
          )}
          {variant === "dialog" && onClose && (
            <Secondary type="button" onClick={onClose}>
              {paid || lifetime ? "Close" : "Not now"}
            </Secondary>
          )}
        </Action>
      </Body>
    </Card>
  );
};
